/**
 * Demo CRM adapter — returns canned fixture data per trade so the live demos at
 * admin.demo-{trade}.aidxn.com show a fully-populated dashboard without any real
 * CRM connection.
 *
 * Selected by `CrmContext` when `tenant.is_demo === true`. Never makes a network
 * call. All mutations (createDeal etc.) are no-ops that return realistic stub
 * objects so the UI's optimistic-update paths don't break.
 *
 * Per-tenant fixtures are picked by tenant slug — `demo-plumbing` →
 * PLUMBING_DEALS, `demo-electricians` → ELECTRICIAN_DEALS, etc.
 */

import type { CrmAdapter } from './adapter';
import type {
  Contact,
  CreateContactInput,
  CreateDealInput,
  CrmFilter,
  CrmMeeting,
  CrmObjectType,
  CustomField,
  Deal,
  ListContactsOpts,
  ListContactsResult,
  ListDealsOpts,
  ListDealsResult,
  ListLeadsOpts,
  ListLeadsResult,
  ListMeetingsOpts,
  ListMeetingsResult,
  Owner,
  UpdateContactInput,
  UpdateDealPatch,
} from './types';

// ── Per-trade fixtures ──────────────────────────────────────────────────────

type TradeKey = 'plumbing' | 'electricians' | 'landscapers' | 'carpenters';

interface TradeDeal {
  name: string;
  amount: number;
  stage: string;
  ownerEmail: string;
  address: string;
  contactName: string;
  contactPhone: string;
  custom: Record<string, unknown>;
}

const PLUMBING_DEALS: TradeDeal[] = [
  { name: 'Hot water replacement — 250L gas storage', amount: 2480, stage: 'Quoted',     ownerEmail: 'gold@example.com',    address: '12 Pacific Ave, Surfers Paradise QLD 4217', contactName: 'Sarah Mitchell',  contactPhone: '0412 334 921', custom: { region: 'GC-NORTH',     verdict: 'in-service', urgency: 'next-day',  lat: -28.0014, lng: 153.4321 } },
  { name: 'Emergency burst pipe — kitchen line',        amount: 1890, stage: 'Won',        ownerEmail: 'burleigh@example.com', address: '47 Goodwin Tce, Burleigh Heads QLD 4220',   contactName: 'James Watanabe', contactPhone: '0431 552 088', custom: { region: 'GC-SOUTH',     verdict: 'in-service', urgency: 'emergency', lat: -28.0928, lng: 153.4519 } },
  { name: 'Gas fitter — new oven install + cert',       amount: 720,  stage: 'Won',        ownerEmail: 'bne@example.com',     address: '3 Sherwood Rd, Toowong QLD 4066',           contactName: 'Mei-Lin Chen',   contactPhone: '0418 776 304', custom: { region: 'BNE-INNER',    verdict: 'in-service', urgency: 'scheduled', lat: -27.4841, lng: 153.0038 } },
  { name: 'Blocked drain — root infiltration',          amount: 1340, stage: 'In Progress', ownerEmail: 'tmba@example.com',   address: '88 Hume St, East Toowoomba QLD 4350',       contactName: 'Pete Donnelly',  contactPhone: '0407 119 663', custom: { region: 'DARLING-DOWNS',verdict: 'in-service', urgency: 'same-week', lat: -27.5657, lng: 151.9672 } },
  { name: 'Bore pump replacement — rural acreage',      amount: 4980, stage: 'Quoted',     ownerEmail: 'tmba@example.com',   address: '320 Old Goombungee Rd, Highfields QLD 4352', contactName: 'Tanya Reid',     contactPhone: '0419 487 215', custom: { region: 'DARLING-DOWNS',verdict: 'in-service', urgency: 'planned',   lat: -27.4661, lng: 151.9391 } },
  { name: 'Frozen pipe callout — winter Drayton',       amount: 950,  stage: 'Won',        ownerEmail: 'tmba@example.com',   address: '15 Stenner St, Drayton QLD 4350',           contactName: 'Adam Becker',    contactPhone: '0466 008 442', custom: { region: 'DARLING-DOWNS',verdict: 'in-service', urgency: 'emergency', lat: -27.5902, lng: 151.9206 } },
  { name: 'Hot water — solar to heat pump swap',        amount: 6200, stage: 'In Discovery', ownerEmail: 'byron@example.com', address: '9 Belongil Cres, Byron Bay NSW 2481',       contactName: 'Olivia Hart',    contactPhone: '0414 909 718', custom: { region: 'NORTHERN-NSW', verdict: 'in-service', urgency: 'planned',   lat: -28.6421, lng: 153.6139 } },
  { name: 'Bathroom reno — full hydraulic rough-in',    amount: 8400, stage: 'Quoted',     ownerEmail: 'byron@example.com',  address: '11 Tweed St, Ballina NSW 2478',             contactName: 'Marcus Wei',     contactPhone: '0422 558 109', custom: { region: 'NORTHERN-NSW', verdict: 'in-service', urgency: 'planned',   lat: -28.8610, lng: 153.5651 } },
  { name: 'Septic backup — overflow Lismore',           amount: 1620, stage: 'Won',        ownerEmail: 'byron@example.com',  address: '203 Ballina Rd, Lismore NSW 2480',          contactName: 'Geraldine Cox',  contactPhone: '0438 661 277', custom: { region: 'NORTHERN-NSW', verdict: 'in-service', urgency: 'same-day',  lat: -28.8145, lng: 153.2756 } },
  { name: 'Storm callout — Centenary Heights',          amount: 1180, stage: 'In Progress', ownerEmail: 'tmba@example.com',  address: '6 Hancock St, Centenary Heights QLD 4350',  contactName: 'Brett Holland',  contactPhone: '0405 882 304', custom: { region: 'DARLING-DOWNS',verdict: 'in-service', urgency: 'same-day',  lat: -27.5801, lng: 151.9731 } },
  { name: 'Commercial backflow audit — cafe Toowong',   amount: 2300, stage: 'Quoted',     ownerEmail: 'bne@example.com',    address: '147 Hawken Dr, Indooroopilly QLD 4068',     contactName: 'Café Lulu (Anna)', contactPhone: '0407 333 121', custom: { region: 'BNE-WEST',     verdict: 'in-service', urgency: 'scheduled', lat: -27.5067, lng: 152.9733 } },
  { name: 'Hot water — 6.6kW heat pump install',        amount: 4990, stage: 'Won',        ownerEmail: 'gold@example.com',   address: '88 Markwell Ave, Robina QLD 4226',          contactName: 'Hayden Pike',    contactPhone: '0411 008 552', custom: { region: 'GC-SOUTH',     verdict: 'in-service', urgency: 'planned',   lat: -28.0797, lng: 153.3964 } },
];

const ELECTRICIAN_DEALS: TradeDeal[] = [
  { name: 'Switchboard upgrade — ceramic to RCD',        amount: 2480, stage: 'Quoted',      ownerEmail: 'gold@example.com',   address: '32 Markeri St, Mermaid Beach QLD 4218',     contactName: 'Sarah Mitchell', contactPhone: '0412 334 921', custom: { region: 'GC-NORTH', verdict: 'in-service', urgency: 'planned',    lat: -28.0451, lng: 153.4438 } },
  { name: 'Emergency callout — total power-out 3am',     amount: 1340, stage: 'Won',         ownerEmail: 'burleigh@example.com', address: '47 Goodwin Tce, Burleigh Heads QLD 4220', contactName: 'James Watanabe', contactPhone: '0431 552 088', custom: { region: 'GC-SOUTH', verdict: 'in-service', urgency: 'emergency',  lat: -28.0898, lng: 153.4551 } },
  { name: 'Solar 6.6kW + 13.5kWh Powerwall install',     amount: 18900, stage: 'In Discovery', ownerEmail: 'tmba@example.com',  address: '320 Old Goombungee Rd, Highfields QLD 4352', contactName: 'Tanya Reid',    contactPhone: '0419 487 215', custom: { region: 'DARLING-DOWNS', verdict: 'in-service', urgency: 'planned',  lat: -27.4628, lng: 151.9423 } },
  { name: 'Tesla wall connector — 22kW 3-phase upgrade', amount: 3850, stage: 'Quoted',      ownerEmail: 'byron@example.com',  address: '9 Belongil Cres, Byron Bay NSW 2481',       contactName: 'Olivia Hart',   contactPhone: '0414 909 718', custom: { region: 'NORTHERN-NSW', verdict: 'in-service', urgency: 'planned',  lat: -28.6455, lng: 153.6104 } },
  { name: 'Commercial fit-out — cafe Indooroopilly',     amount: 14600, stage: 'In Progress', ownerEmail: 'bne@example.com',   address: '147 Hawken Dr, Indooroopilly QLD 4068',     contactName: 'Café Lulu (Anna)', contactPhone: '0407 333 121', custom: { region: 'BNE-WEST', verdict: 'in-service', urgency: 'scheduled',  lat: -27.5033, lng: 152.9767 } },
  { name: 'Smoke alarm compliance — full rental',        amount: 880,  stage: 'Won',         ownerEmail: 'bne@example.com',    address: '12 Latrobe Tce, Paddington QLD 4064',       contactName: 'Mei-Lin Chen',  contactPhone: '0418 776 304', custom: { region: 'BNE-INNER', verdict: 'in-service', urgency: 'compliance', lat: -27.4618, lng: 153.0042 } },
  { name: 'Level-2 ASP — NMI re-meter and pole hookup',  amount: 2150, stage: 'Won',         ownerEmail: 'tmba@example.com',   address: '15 Stenner St, Drayton QLD 4350',           contactName: 'Adam Becker',   contactPhone: '0466 008 442', custom: { region: 'DARLING-DOWNS', verdict: 'in-service', urgency: 'planned',  lat: -27.5935, lng: 151.9173 } },
  { name: 'Solar inverter replacement — out-of-warranty',amount: 2890, stage: 'Quoted',      ownerEmail: 'gold@example.com',   address: '88 Markwell Ave, Robina QLD 4226',          contactName: 'Hayden Pike',   contactPhone: '0411 008 552', custom: { region: 'GC-SOUTH', verdict: 'in-service', urgency: 'planned',    lat: -28.0832, lng: 153.3932 } },
  { name: 'EV wallbox + dedicated circuit',              amount: 2480, stage: 'Won',         ownerEmail: 'byron@example.com',  address: '11 Tweed St, Ballina NSW 2478',             contactName: 'Marcus Wei',    contactPhone: '0422 558 109', custom: { region: 'NORTHERN-NSW', verdict: 'in-service', urgency: 'planned',  lat: -28.8643, lng: 153.5618 } },
  { name: 'Storm callout — fallen line East Toowoomba',  amount: 1450, stage: 'Won',         ownerEmail: 'tmba@example.com',   address: '88 Hume St, East Toowoomba QLD 4350',       contactName: 'Pete Donnelly', contactPhone: '0407 119 663', custom: { region: 'DARLING-DOWNS', verdict: 'in-service', urgency: 'emergency', lat: -27.5624, lng: 151.9641 } },
  { name: 'Off-grid hybrid — 11kW + lithium storage',    amount: 32400, stage: 'In Discovery', ownerEmail: 'byron@example.com',address: '203 Ballina Rd, Lismore NSW 2480',          contactName: 'Geraldine Cox', contactPhone: '0438 661 277', custom: { region: 'NORTHERN-NSW', verdict: 'in-service', urgency: 'planned',  lat: -28.8112, lng: 153.2789 } },
  { name: 'Office data + power — 12-desk fitout',        amount: 7800, stage: 'Quoted',      ownerEmail: 'bne@example.com',    address: '3 Sherwood Rd, Toowong QLD 4066',           contactName: 'Brett Holland', contactPhone: '0405 882 304', custom: { region: 'BNE-INNER', verdict: 'in-service', urgency: 'planned',    lat: -27.4870, lng: 153.0011 } },
];

const LANDSCAPER_DEALS: TradeDeal[] = [
  { name: 'Front yard redesign — native palette',        amount: 12800, stage: 'In Discovery', ownerEmail: 'gold@example.com',  address: '32 Markeri St, Mermaid Beach QLD 4218',     contactName: 'Sarah Mitchell', contactPhone: '0412 334 921', custom: { region: 'GC-NORTH', verdict: 'in-service', urgency: 'planned',   lat: -28.0419, lng: 153.4470 } },
  { name: 'Sandstone retaining wall — 14m × 600mm',      amount: 18900, stage: 'Quoted',       ownerEmail: 'burleigh@example.com', address: '47 Goodwin Tce, Burleigh Heads QLD 4220', contactName: 'James Watanabe', contactPhone: '0431 552 088', custom: { region: 'GC-SOUTH', verdict: 'in-service', urgency: 'planned',   lat: -28.0939, lng: 153.4510 } },
  { name: 'Hydrawise irrigation — 6-zone retrofit',      amount: 3450, stage: 'Won',          ownerEmail: 'bne@example.com',     address: '147 Hawken Dr, Indooroopilly QLD 4068',     contactName: 'Mei-Lin Chen',   contactPhone: '0418 776 304', custom: { region: 'BNE-WEST', verdict: 'in-service', urgency: 'planned',   lat: -27.5078, lng: 152.9722 } },
  { name: 'Fortnightly maintenance — quarter-acre',      amount: 1320, stage: 'Won',          ownerEmail: 'bne@example.com',     address: '12 Latrobe Tce, Paddington QLD 4064',       contactName: 'Pete Donnelly',  contactPhone: '0407 119 663', custom: { region: 'BNE-INNER', verdict: 'in-service', urgency: 'recurring', lat: -27.4647, lng: 153.0011 } },
  { name: 'Pool surround — travertine + drainage',       amount: 16400, stage: 'Quoted',      ownerEmail: 'gold@example.com',   address: '88 Markwell Ave, Robina QLD 4226',          contactName: 'Hayden Pike',    contactPhone: '0411 008 552', custom: { region: 'GC-SOUTH', verdict: 'in-service', urgency: 'planned',   lat: -28.0846, lng: 153.3979 } },
  { name: 'Permaculture install — Byron acreage',        amount: 24800, stage: 'In Progress',  ownerEmail: 'byron@example.com',  address: '9 Belongil Cres, Byron Bay NSW 2481',       contactName: 'Olivia Hart',    contactPhone: '0414 909 718', custom: { region: 'NORTHERN-NSW', verdict: 'in-service', urgency: 'planned',   lat: -28.6403, lng: 153.6157 } },
  { name: 'Native garden — Toowoomba heritage cottage',  amount: 8900, stage: 'Quoted',       ownerEmail: 'tmba@example.com',    address: '88 Hume St, East Toowoomba QLD 4350',       contactName: 'Tanya Reid',     contactPhone: '0419 487 215', custom: { region: 'DARLING-DOWNS', verdict: 'in-service', urgency: 'planned',   lat: -27.5673, lng: 151.9688 } },
  { name: 'Exposed-aggregate driveway — 90sqm',          amount: 7400, stage: 'Won',          ownerEmail: 'tmba@example.com',   address: '15 Stenner St, Drayton QLD 4350',           contactName: 'Adam Becker',    contactPhone: '0466 008 442', custom: { region: 'DARLING-DOWNS', verdict: 'in-service', urgency: 'planned',   lat: -27.5887, lng: 151.9221 } },
  { name: 'Vegetable garden beds — 4 × 3m raised',       amount: 2980, stage: 'Won',          ownerEmail: 'byron@example.com',  address: '11 Tweed St, Ballina NSW 2478',             contactName: 'Marcus Wei',     contactPhone: '0422 558 109', custom: { region: 'NORTHERN-NSW', verdict: 'in-service', urgency: 'planned',   lat: -28.8595, lng: 153.5667 } },
  { name: 'Hedge + bed maintenance — monthly',           amount: 880,  stage: 'Won',          ownerEmail: 'tmba@example.com',   address: '6 Hancock St, Centenary Heights QLD 4350',  contactName: 'Brett Holland',  contactPhone: '0405 882 304', custom: { region: 'DARLING-DOWNS', verdict: 'in-service', urgency: 'recurring', lat: -27.5835, lng: 151.9697 } },
  { name: 'Permeable paver crossover — council spec',    amount: 5400, stage: 'Quoted',       ownerEmail: 'gold@example.com',   address: '3 Sherwood Rd, Toowong QLD 4066',           contactName: 'Café Lulu (Anna)', contactPhone: '0407 333 121', custom: { region: 'GC-NORTH', verdict: 'in-service', urgency: 'planned',   lat: -27.4825, lng: 153.0058 } },
  { name: 'Greywater redirect + new lawn',               amount: 6900, stage: 'In Discovery', ownerEmail: 'byron@example.com',  address: '203 Ballina Rd, Lismore NSW 2480',          contactName: 'Geraldine Cox',  contactPhone: '0438 661 277', custom: { region: 'NORTHERN-NSW', verdict: 'in-service', urgency: 'planned',   lat: -28.8161, lng: 153.2740 } },
];

const CARPENTER_DEALS: TradeDeal[] = [
  { name: 'Merbau deck — 38sqm with cabana',             amount: 32400, stage: 'Quoted',       ownerEmail: 'gold@example.com',  address: '32 Markeri St, Mermaid Beach QLD 4218',     contactName: 'Sarah Mitchell', contactPhone: '0412 334 921', custom: { region: 'GC-NORTH', verdict: 'in-service', urgency: 'planned', lat: -28.0428, lng: 153.4422 } },
  { name: 'Spotted gum entertaining deck — sloping block', amount: 28900, stage: 'In Discovery', ownerEmail: 'burleigh@example.com', address: '47 Goodwin Tce, Burleigh Heads QLD 4220', contactName: 'James Watanabe', contactPhone: '0431 552 088', custom: { region: 'GC-SOUTH', verdict: 'in-service', urgency: 'planned', lat: -28.0884, lng: 153.4561 } },
  { name: 'Shaker kitchen — 2-pack polyurethane',        amount: 24600, stage: 'In Progress',  ownerEmail: 'bne@example.com',    address: '12 Latrobe Tce, Paddington QLD 4064',       contactName: 'Mei-Lin Chen',  contactPhone: '0418 776 304', custom: { region: 'BNE-INNER', verdict: 'in-service', urgency: 'planned', lat: -27.4604, lng: 153.0049 } },
  { name: 'Walk-in robe joinery — full house',           amount: 14800, stage: 'Won',          ownerEmail: 'bne@example.com',   address: '147 Hawken Dr, Indooroopilly QLD 4068',     contactName: 'Pete Donnelly', contactPhone: '0407 119 663', custom: { region: 'BNE-WEST', verdict: 'in-service', urgency: 'planned', lat: -27.5022, lng: 152.9782 } },
  { name: 'Gable pergola — polycarbonate roof',          amount: 9800, stage: 'Quoted',       ownerEmail: 'gold@example.com',  address: '88 Markwell Ave, Robina QLD 4226',          contactName: 'Hayden Pike',   contactPhone: '0411 008 552', custom: { region: 'GC-SOUTH', verdict: 'in-service', urgency: 'planned', lat: -28.0779, lng: 153.3915 } },
  { name: 'BAL-29 deck — Byron hinterland',              amount: 38400, stage: 'In Progress',  ownerEmail: 'byron@example.com', address: '9 Belongil Cres, Byron Bay NSW 2481',       contactName: 'Olivia Hart',   contactPhone: '0414 909 718', custom: { region: 'NORTHERN-NSW', verdict: 'in-service', urgency: 'planned', lat: -28.6471, lng: 153.6088 } },
  { name: 'Knock-down rebuild — first-floor frame',      amount: 86400, stage: 'In Discovery', ownerEmail: 'tmba@example.com', address: '88 Hume St, East Toowoomba QLD 4350',       contactName: 'Tanya Reid',    contactPhone: '0419 487 215', custom: { region: 'DARLING-DOWNS', verdict: 'in-service', urgency: 'planned', lat: -27.5612, lng: 151.9633 } },
  { name: 'Garage second-storey extension',              amount: 124800, stage: 'Quoted',     ownerEmail: 'tmba@example.com',  address: '15 Stenner St, Drayton QLD 4350',           contactName: 'Adam Becker',   contactPhone: '0466 008 442', custom: { region: 'DARLING-DOWNS', verdict: 'in-service', urgency: 'planned', lat: -27.5947, lng: 151.9164 } },
  { name: 'Bifold servery + deck blend',                 amount: 11600, stage: 'Won',         ownerEmail: 'byron@example.com', address: '11 Tweed St, Ballina NSW 2478',             contactName: 'Marcus Wei',    contactPhone: '0422 558 109', custom: { region: 'NORTHERN-NSW', verdict: 'in-service', urgency: 'planned', lat: -28.8658, lng: 153.5602 } },
  { name: 'Custom front-entry pivot door',               amount: 7800, stage: 'Won',          ownerEmail: 'tmba@example.com', address: '6 Hancock St, Centenary Heights QLD 4350',  contactName: 'Brett Holland', contactPhone: '0405 882 304', custom: { region: 'DARLING-DOWNS', verdict: 'in-service', urgency: 'planned', lat: -27.5849, lng: 151.9732 } },
  { name: 'Mudroom + butler\'s pantry joinery',          amount: 18600, stage: 'In Progress', ownerEmail: 'bne@example.com',   address: '3 Sherwood Rd, Toowong QLD 4066',           contactName: 'Café Lulu (Anna)', contactPhone: '0407 333 121', custom: { region: 'BNE-INNER', verdict: 'in-service', urgency: 'planned', lat: -27.4880, lng: 152.9997 } },
  { name: 'Cottage extension — full reno',               amount: 162000, stage: 'In Discovery', ownerEmail: 'byron@example.com', address: '203 Ballina Rd, Lismore NSW 2480',        contactName: 'Geraldine Cox', contactPhone: '0438 661 277', custom: { region: 'NORTHERN-NSW', verdict: 'in-service', urgency: 'planned', lat: -28.8094, lng: 153.2803 } },
];

const FIXTURE_BY_TRADE: Record<TradeKey, TradeDeal[]> = {
  plumbing:     PLUMBING_DEALS,
  electricians: ELECTRICIAN_DEALS,
  landscapers:  LANDSCAPER_DEALS,
  carpenters:   CARPENTER_DEALS,
};

const DEMO_OWNERS: Owner[] = [
  { id: 'demo-owner-1', email: 'gold@example.com',     firstName: 'Dave',  lastName: 'Mitchell'   },
  { id: 'demo-owner-2', email: 'burleigh@example.com', firstName: 'Jake',  lastName: 'Reed'       },
  { id: 'demo-owner-3', email: 'bne@example.com',      firstName: 'Tom',   lastName: 'Barker'     },
  { id: 'demo-owner-4', email: 'tmba@example.com',     firstName: 'Pete',  lastName: 'Donnelly'   },
  { id: 'demo-owner-5', email: 'byron@example.com',    firstName: 'Olivia', lastName: 'Hart'      },
];

const DEMO_FILTERS: CrmFilter[] = [
  { id: 'demo-filter-active',    name: 'Active pipeline',         objectType: 'deal' },
  { id: 'demo-filter-won-30d',   name: 'Won in last 30 days',     objectType: 'deal' },
  { id: 'demo-filter-emergency', name: 'Emergency callouts only', objectType: 'deal' },
  { id: 'demo-filter-quoted',    name: 'Quoted — needs follow-up', objectType: 'deal' },
];

const DEMO_FIELDS: CustomField[] = [
  { name: 'region',   label: 'Service Region',  type: 'enum',   options: ['GC-NORTH', 'GC-SOUTH', 'BNE-INNER', 'BNE-WEST', 'BNE-EAST', 'BNE-NORTH', 'DARLING-DOWNS', 'NORTHERN-NSW'] },
  { name: 'verdict',  label: 'Verdict',          type: 'enum',   options: ['in-service', 'out-of-area', 'needs-quote'] },
  { name: 'urgency',  label: 'Callout Urgency',  type: 'enum',   options: ['emergency', 'same-day', 'same-week', 'planned', 'scheduled', 'compliance', 'recurring', 'next-day'] },
];

// ── Helpers ─────────────────────────────────────────────────────────────────

function tradeKeyFromSlug(slug: string): TradeKey {
  if (slug.includes('plumbing'))     return 'plumbing';
  if (slug.includes('electricians')) return 'electricians';
  if (slug.includes('landscapers'))  return 'landscapers';
  if (slug.includes('carpenters'))   return 'carpenters';
  // Generic fallback — plumbing has the broadest mix.
  return 'plumbing';
}

function tradeDealToDeal(t: TradeDeal, idx: number, tradeKey: TradeKey): Deal {
  // Stable IDs across reloads.
  const id = `demo-${tradeKey}-${idx + 1}`;
  // Spread created/updated across the last 30 days for visual variety.
  const createdAt = new Date(Date.now() - (idx + 1) * 36 * 60 * 60 * 1000);
  const updatedAt = new Date(Date.now() - idx * 4 * 60 * 60 * 1000);
  return {
    id,
    name: t.name,
    amount: t.amount,
    stageId: `stage-${t.stage.toLowerCase().replace(/\s+/g, '-')}`,
    stageName: t.stage,
    ownerId: DEMO_OWNERS.find((o) => o.email === t.ownerEmail)?.id ?? null,
    address: t.address,
    contactName: t.contactName,
    contactPhone: t.contactPhone,
    customFields: t.custom,
    createdAt,
    updatedAt,
  };
}

// ── Adapter ─────────────────────────────────────────────────────────────────

/**
 * Build a demo adapter pinned to a specific tenant slug. Same shape as the
 * HubSpot/Attio adapters so `CrmContext` can swap it in transparently.
 *
 * Reports `type: 'hubspot'` to keep the existing CrmType union unchanged.
 * The UI can still detect demo mode via `tenant.is_demo`.
 */
export function createDemoAdapter(tenantSlug: string): CrmAdapter {
  const tradeKey = tradeKeyFromSlug(tenantSlug);
  const fixtures = FIXTURE_BY_TRADE[tradeKey];
  const deals = fixtures.map((t, i) => tradeDealToDeal(t, i, tradeKey));

  return {
    type: 'hubspot',

    async listDeals(opts?: ListDealsOpts): Promise<ListDealsResult> {
      const pageSize = opts?.pageSize ?? 50;
      let filtered = deals;
      if (opts?.filterId === 'demo-filter-won-30d') {
        filtered = deals.filter((d) => d.stageName === 'Won');
      } else if (opts?.filterId === 'demo-filter-emergency') {
        filtered = deals.filter((d) => d.customFields.urgency === 'emergency');
      } else if (opts?.filterId === 'demo-filter-quoted') {
        filtered = deals.filter((d) => d.stageName === 'Quoted');
      }
      return { items: filtered.slice(0, pageSize), nextCursor: null };
    },

    async listOwners(): Promise<Owner[]> {
      return DEMO_OWNERS;
    },

    async listFilters(): Promise<CrmFilter[]> {
      return DEMO_FILTERS;
    },

    async listCustomFields(_objectType: CrmObjectType): Promise<CustomField[]> {
      return DEMO_FIELDS;
    },

    async getDeal(id: string): Promise<Deal | null> {
      return deals.find((d) => d.id === id) ?? null;
    },

    async createDeal(input: CreateDealInput): Promise<Deal> {
      // Realistic stub — returns a Deal shaped like the others without persisting.
      const newDeal: Deal = {
        id: `demo-${tradeKey}-new-${Date.now()}`,
        name: input.name,
        amount: input.amount ?? 0,
        stageId: input.stageId ?? 'stage-quoted',
        stageName: 'Quoted',
        ownerId: null,
        address: null,
        contactName: null,
        contactPhone: null,
        customFields: input.customFields ?? {},
        createdAt: new Date(),
        updatedAt: new Date(),
      };
      return newDeal;
    },

    async updateDeal(id: string, patch: UpdateDealPatch): Promise<Deal> {
      const existing = deals.find((d) => d.id === id) ?? deals[0];
      return {
        ...existing,
        ...(patch.name != null ? { name: patch.name } : {}),
        ...(patch.amount != null ? { amount: patch.amount } : {}),
        ...(patch.stageId != null ? { stageId: patch.stageId } : {}),
        updatedAt: new Date(),
      };
    },

    async assignOwner(_dealId: string, _ownerId: string): Promise<void> {
      // No-op in demo.
    },

    async setCustomField(_dealId: string, _fieldName: string, _value: unknown): Promise<void> {
      // No-op in demo.
    },

    async listMeetings(_opts?: ListMeetingsOpts): Promise<ListMeetingsResult> {
      // CRM-sourced meetings live in v9_calendar_events_cache (seeded server-side
      // for each demo tenant), so the calendar tab in the right sidebar already
      // shows them via the standard Phase 10 pipeline. Returning an empty list
      // here avoids double-counting.
      const items: CrmMeeting[] = [];
      return { items };
    },

    // ── Contact CRUD (demo stubs) ────────────────────────────────────────
    // Synthesise contacts from each deal's contactName/contactPhone so the demo
    // dashboards have something to render. Mutations are no-ops returning realistic
    // shapes so optimistic UI paths don't break.
    async listContacts(_opts?: ListContactsOpts): Promise<ListContactsResult> {
      const items: Contact[] = deals
        .filter((d) => d.contactName)
        .map((d, i) => {
          const [firstName = null, ...rest] = (d.contactName ?? '').split(' ');
          const lastName = rest.length > 0 ? rest.join(' ') : null;
          return {
            id: `demo-${tradeKey}-contact-${i}`,
            firstName,
            lastName,
            email: null,
            phone: d.contactPhone,
            company: null,
            lifecycleStage: null,
            address: null,
            customFields: {},
            createdAt: d.createdAt,
            updatedAt: d.updatedAt,
          };
        });
      return { items, nextCursor: null };
    },

    async getContact(id: string): Promise<Contact | null> {
      const items = (await this.listContacts()).items;
      return items.find((c) => c.id === id) ?? null;
    },

    async createContact(input: CreateContactInput): Promise<Contact> {
      return {
        id: `demo-${tradeKey}-contact-new-${Date.now()}`,
        firstName: input.firstName ?? null,
        lastName: input.lastName ?? null,
        email: input.email ?? null,
        phone: input.phone ?? null,
        company: input.company ?? null,
        lifecycleStage: null,
        address: null,
        customFields: input.customFields ?? {},
        createdAt: new Date(),
        updatedAt: new Date(),
      };
    },

    async updateContact(id: string, patch: UpdateContactInput): Promise<Contact> {
      const existing = (await this.getContact(id)) ?? {
        id,
        firstName: null,
        lastName: null,
        email: null,
        phone: null,
        company: null,
        lifecycleStage: null,
        address: null,
        customFields: {},
        createdAt: new Date(),
        updatedAt: new Date(),
      };
      return {
        ...existing,
        ...(patch.firstName !== undefined ? { firstName: patch.firstName ?? null } : {}),
        ...(patch.lastName !== undefined ? { lastName: patch.lastName ?? null } : {}),
        ...(patch.email !== undefined ? { email: patch.email ?? null } : {}),
        ...(patch.phone !== undefined ? { phone: patch.phone ?? null } : {}),
        ...(patch.company !== undefined ? { company: patch.company ?? null } : {}),
        ...(patch.customFields ? { customFields: { ...existing.customFields, ...patch.customFields } } : {}),
        updatedAt: new Date(),
      };
    },

    async listLeads(_opts?: ListLeadsOpts): Promise<ListLeadsResult> {
      // Return 4 synthetic leads: Brisbane suburbs, mix of lifecycle stages, 2 with addresses.
      const leads: Contact[] = [
        {
          id: `demo-${tradeKey}-lead-1`,
          firstName: 'Alex',
          lastName: 'Johnson',
          email: 'alex.johnson@example.com',
          phone: '0412 555 111',
          company: 'Tech Startup',
          lifecycleStage: 'lead',
          address: '12 Wattle St, Chermside QLD 4032',
          customFields: {},
          createdAt: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000),
          updatedAt: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000),
        },
        {
          id: `demo-${tradeKey}-lead-2`,
          firstName: 'Sam',
          lastName: 'Williams',
          email: 'sam.w@example.com',
          phone: '0431 666 222',
          company: 'BuildCorp',
          lifecycleStage: 'subscriber',
          address: null,
          customFields: {},
          createdAt: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000),
          updatedAt: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000),
        },
        {
          id: `demo-${tradeKey}-lead-3`,
          firstName: 'Jordan',
          lastName: 'Smith',
          email: 'jordan@example.com',
          phone: '0433 777 333',
          company: null,
          lifecycleStage: 'lead',
          address: '45 Mountain View, Sunnybank QLD 4109',
          customFields: {},
          createdAt: new Date(Date.now() - 4 * 24 * 60 * 60 * 1000),
          updatedAt: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000),
        },
        {
          id: `demo-${tradeKey}-lead-4`,
          firstName: 'Taylor',
          lastName: 'Brown',
          email: 'taylor.brown@example.com',
          phone: null,
          company: 'Design Studio',
          lifecycleStage: 'subscriber',
          address: null,
          customFields: {},
          createdAt: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000),
          updatedAt: new Date(Date.now() - 6 * 60 * 60 * 1000),
        },
      ];
      return { items: leads, nextCursor: null };
    },
  };
}

export function isDemoTradeKey(slug: string): boolean {
  return /^demo-(plumbing|electricians|landscapers|carpenters)$/.test(slug);
}
